import React, { useState, useEffect } from 'react';
import { Box, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { codeToTokens } from 'shiki';
import type { Code as Interface } from '../../design/components';

export const Code: React.FC<Interface> = ({
  name, description, state, hidden, inline, readonly, disabled, required, error, multiline = 14
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [focused, setFocused] = useState(false);
  const [tokens, setTokens] = useState<{ content: string; color?: string }[][]>([]);

  useEffect(() => {
    const code = value || '';
    if (!code) {
      // setState-in-effect is intentional: tokens are derived from value;
      // an empty value synchronously clears them. No cascade risk — the
      // effect depends on `value` alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTokens([]);
      return;
    }
    codeToTokens(code, { lang: 'javascript', theme: 'github-light' })
      .then(result => {
        setTokens(result.tokens.map(line =>
          line.map(token => ({ content: token.content, color: token.color }))
        ));
      })
      .catch(() => setTokens([]));
  }, [value]);

  // Inline mode: render just the code as monospace text with highlighting
  if (inline) {
    return (
      <Box
        component="code"
        sx={{
          display: hidden ? 'none' : 'inline',
          fontFamily: 'monospace',
          fontSize: '0.875em',
          whiteSpace: 'pre-wrap',
        }}
      >
        {tokens.length > 0 ? (
          tokens[0]?.map((token, i) => (
            <span key={i} style={{ color: token.color }}>
              {token.content}
            </span>
          ))
        ) : (value || '')}
      </Box>
    );
  }

  const baseStyles = {
    fontFamily: 'monospace',
    fontSize: '16px',
    lineHeight: '24px',
    tabSize: 2,
    margin: 0,
    padding: '15px 14px',
    border: 0,
    whiteSpace: 'pre-wrap' as const,
    wordWrap: 'break-word' as const,
  };

  const actualLines = (value || '').split('\n').length;
  const lineCount = Math.min(Math.max(actualLines, 1), multiline);
  const atMax = actualLines > multiline;
  const lineHeightPx = 24; // 16px font * 1.5 line-height
  const maxHeight = atMax ? `${multiline * lineHeightPx + 33}px` : undefined; // 33px for padding (16.5 * 2)

  const hasValue = !!value;
  const shrink = focused || hasValue;

  return (
    <FormControl fullWidth error={!!error} required={required} focused={focused} sx={{ display: hidden ? 'none' : undefined, mb: 2 }}>
      <InputLabel
        htmlFor={name}
        shrink={shrink}
        sx={{
          bgcolor: shrink ? 'background.paper' : 'transparent',
          px: shrink ? 0.5 : 0,
          ...(!shrink && {
            transform: 'translate(14px, 16px) scale(1)',
            color: 'text.secondary',
          }),
        }}
      >
        {name}
      </InputLabel>
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          borderRadius: 1,
          border: focused ? '2px solid' : '1px solid',
          borderColor: error ? 'error.main' : focused ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
          margin: focused ? 0 : '1px',
          maxHeight,
          overflow: atMax ? 'auto' : undefined,
          minHeight: '56px',
        }}
      >
        {/* Highlighted code layer (behind) */}
        <Box
          component="pre"
          aria-hidden="true"
          sx={{
            ...baseStyles,
            gridArea: '1 / 1 / 2 / 2',
            overflow: 'hidden',
            pointerEvents: 'none',
            bgcolor: 'transparent',
          }}
        >
          {tokens.length > 0 ? (
            tokens.map((line, lineIdx) => (
              <React.Fragment key={lineIdx}>
                {line.map((token, tokenIdx) => (
                  <span key={tokenIdx} style={{ color: token.color }}>
                    {token.content}
                  </span>
                ))}
                {lineIdx < tokens.length - 1 && '\n'}
              </React.Fragment>
            ))
          ) : (value || '') + '\n'}
        </Box>
        {/* Transparent textarea (in front for input) */}
        <Box
          component="textarea"
          id={name}
          name={name}
          value={value || ''}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          rows={lineCount}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          spellCheck={false}
          placeholder={focused && !hasValue ? 'code' : undefined}
          sx={{
            ...baseStyles,
            gridArea: '1 / 1 / 2 / 2',
            width: '100%',
            boxSizing: 'border-box',
            resize: 'none',
            bgcolor: 'transparent',
            color: 'transparent',
            caretColor: 'black',
            outline: 'none',
            '&::selection': {
              bgcolor: 'primary.light',
            },
            '&:disabled': {
              bgcolor: 'grey.200',
            },
            '&::placeholder': {
              color: '#a1a1a1',
              opacity: 1,
            },
          }}
        />
      </Box>
      {(error || description) && (
        <FormHelperText>{error || description}</FormHelperText>
      )}
    </FormControl>
  );
};
